'use client';

import { Session } from '@pickleballinc/lib/session';
import { PaginationV2 } from '@pickleballinc/react-ui';
import { useSession } from '@pickleballinc/ui/components/global/SessionProvider';
import { useRouter, useSearchParams } from 'next/navigation';

import { useCreateTournamentURL } from '@/hooks/players/useCreateTournamentURL';
import AccessLimited from '@/modules/players/v2/tabs/components/AccessLimited';
import { ALL_MODULES_PAGE_SIZE } from '@/modules/players/v2/tabs/constants';
import { FavoriteTournaments } from '@/modules/players/v2/tabs/tournaments/FavoriteTournaments';
import { ManageTournaments } from '@/modules/players/v2/tabs/tournaments/ManageTournaments';
import { PastTournaments } from '@/modules/players/v2/tabs/tournaments/PastTournaments';
import { UpcomingTournaments } from '@/modules/players/v2/tabs/tournaments/UpcomingTournaments';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import {
  PlayerData,
  PlayerParticipationType,
  playerParticipationTypeMap,
} from '@/types/players.types';
import {
  PlayerTournamentsData,
  statusMap,
  TournamentStatus,
} from '@/types/playerTournaments.types';
import { GetPBApproveToCreatePermissionRes } from '@/utils/actions/leagues/getPBApproveToCreatePermission';

interface PlayerTournamentsWrapperProps {
  player: PlayerData | undefined;
  currentData: PlayerTournamentsData[];
  user?: Session;
  totalRecords: number;
  createPerms: GetPBApproveToCreatePermissionRes;
}

export const PlayerTournamentsWrapper = ({
  player,
  currentData,
  user,
  totalRecords,
  createPerms,
}: PlayerTournamentsWrapperProps) => {
  const router = useRouter();
  const { session } = useSession();
  const { hasPermission } = useAuth();
  const isPickleballSuperAdmin = hasPermission(Permission.SUPER_ADMIN);
  const isPBSuperAdmin = hasPermission(Permission.PICKLEBALL_BRACKETS_ADMIN);
  const isSuperAdmin = isPBSuperAdmin || isPickleballSuperAdmin;
  const isUserAlsoPlayer = user?.uuid.toLowerCase() === player?.uuid.toLowerCase();

  const searchParams = useSearchParams();
  const page = Number(searchParams?.get('page') || 1);
  const upcomingTournaments = searchParams?.get('upcoming') || 'true';
  const searchParamForTourney = searchParams?.get('tournament_type') || 'registeredfor';

  const createTournamentURL = useCreateTournamentURL(session, createPerms);
  const currentTournamentType = playerParticipationTypeMap[searchParamForTourney];
  const currentTournamentStatus = statusMap[upcomingTournaments];

  const isManage = currentTournamentType === PlayerParticipationType.Managing;
  const isFavorites = currentTournamentType === PlayerParticipationType.Favorited;
  const isRegistered = currentTournamentType === PlayerParticipationType.RegisteredFor;

  const isFuture = currentTournamentStatus === TournamentStatus.Future;

  const canManageTourney = (isManage || isFavorites) && (isUserAlsoPlayer || isSuperAdmin);

  const totalPages = Math.ceil(totalRecords / ALL_MODULES_PAGE_SIZE);

  const handlePageChange = (newPage: number) => {
    router.replace(
      `?upcoming=${upcomingTournaments}&tournament_type=${searchParamForTourney}&page=${newPage}`
    );
  };

  return (
    <div className='flex flex-col items-center gap-6 sm:gap-10 justify-center'>
      {isRegistered ? (
        <>
          {isFuture ? (
            <UpcomingTournaments
              data={currentData}
              isUserAlsoPlayer={isUserAlsoPlayer}
              player={player}
            />
          ) : (
            <PastTournaments
              data={currentData}
              player={player}
              isUserAlsoPlayer={isUserAlsoPlayer}
            />
          )}
          {currentData?.length > 0 && totalPages > 1 ? (
            <PaginationV2
              totalNumberOfPages={totalPages}
              currentPage={page}
              onPreviousClick={() => handlePageChange(page - 1)}
              onNextClick={() => handlePageChange(page + 1)}
              onPageClick={(newPage) => handlePageChange(newPage)}
            />
          ) : null}
        </>
      ) : null}
      {isFavorites ? (
        !canManageTourney ? (
          <AccessLimited />
        ) : (
          <>
            <FavoriteTournaments
              data={currentData}
              player={player}
              isUserAlsoPlayer={isUserAlsoPlayer}
            />
            {currentData?.length > 0 && totalPages > 1 ? (
              <PaginationV2
                totalNumberOfPages={totalPages}
                currentPage={page}
                onPreviousClick={() => handlePageChange(page - 1)}
                onNextClick={() => handlePageChange(page + 1)}
                onPageClick={(newPage) => handlePageChange(newPage)}
              />
            ) : null}
          </>
        )
      ) : null}
      {isManage && canManageTourney ? (
        <ManageTournaments
          data={currentData}
          createTournamentUrl={createTournamentURL.toString()}
          player={player}
          isUserAlsoPlayer={isUserAlsoPlayer}
        />
      ) : isManage && !canManageTourney ? (
        <AccessLimited />
      ) : null}
    </div>
  );
};
